import {AppSettingsNavConfig} from '@app/admin/admin-config';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {message} from '@ui/i18n/message';
import {MessageDescriptor} from '@ui/i18n/message-descriptor';
import type {LucideIcon} from 'lucide-react';
import {
  ChartColumnBigIcon,
  CircleDollarSignIcon,
  CodeIcon,
  EarthIcon,
  GlobeIcon,
  HardDriveIcon,
  KeyRoundIcon,
  LayoutTemplateIcon,
  MailIcon,
  MailsIcon,
  MegaphoneIcon,
  MenuIcon,
  PaletteIcon,
  ScaleIcon,
  SearchIcon,
  ServerIcon,
  SettingsIcon,
  ShieldIcon,
  UploadIcon,
} from 'lucide-react';

export type SettingsNavGroupId =
  | 'site'
  | 'drive'
  | 'billing'
  | 'access'
  | 'system';

export interface SettingsNavItem {
  label: MessageDescriptor;
  to: string;
  position?: number;
  icon?: LucideIcon;
  group?: SettingsNavGroupId;
}

export const SettingsNavGroups: {
  id: SettingsNavGroupId;
  label: MessageDescriptor;
}[] = [
  {id: 'site', label: message('Site')},
  {id: 'drive', label: message('Drive')},
  {id: 'billing', label: message('Billing')},
  {id: 'access', label: message('Access')},
  {id: 'system', label: message('System')},
];

const commonNavConfig: (SettingsNavItem | false)[] = [
  {
    label: message('General'),
    to: 'general',
    position: 1,
    icon: SettingsIcon,
    group: 'site',
  },
  {
    label: message('Landing page'),
    to: 'landing-page',
    position: 2,
    icon: LayoutTemplateIcon,
    group: 'site',
  },
  {
    label: message('Themes'),
    to: 'themes',
    position: 3,
    icon: PaletteIcon,
    group: 'site',
  },
  {
    label: message('Menus'),
    to: 'menus',
    position: 4,
    icon: MenuIcon,
    group: 'site',
  },
  {
    label: message('Languages'),
    to: 'localization',
    position: 5,
    icon: GlobeIcon,
    group: 'site',
  },
  {
    label: message('Countries'),
    to: 'countries',
    position: 6,
    icon: EarthIcon,
    group: 'site',
  },
  {label: message('Drive'), to: 'drive', position: 1, icon: HardDriveIcon, group: 'drive'},
  {
    label: message('Uploading'),
    to: 'uploading',
    position: 2,
    icon: UploadIcon,
    group: 'drive',
  },
  !!getBootstrapData().settings.billing?.integrated && {
    label: message('Subscriptions'),
    to: 'subscriptions',
    position: 1,
    icon: CircleDollarSignIcon,
    group: 'billing',
  },
  {
    label: message('Ads'),
    to: 'ads',
    position: 2,
    icon: MegaphoneIcon,
    group: 'billing',
  },
  {
    label: message('Authentication'),
    to: 'authentication',
    position: 1,
    icon: KeyRoundIcon,
    group: 'access',
  },
  {label: message('Captcha'), to: 'captcha', position: 2, icon: ShieldIcon, group: 'access'},
  {label: message('GDPR'), to: 'gdpr', position: 3, icon: ScaleIcon, group: 'access'},
  {label: message('API & webhooks'), to: 'api', position: 4, icon: CodeIcon, group: 'access'},
  {label: message('Email'), to: 'email', position: 1, icon: MailIcon, group: 'system'},
  {
    label: message('Templates'),
    to: 'templates',
    position: 2,
    icon: MailsIcon,
    group: 'system',
  },
  {label: message('System'), to: 'system', position: 3, icon: ServerIcon, group: 'system'},
  {
    label: message('Analytics'),
    to: 'analytics',
    position: 4,
    icon: ChartColumnBigIcon,
    group: 'system',
  },
  {
    label: message('Custom code'),
    to: 'custom-code',
    position: 5,
    icon: CodeIcon,
    group: 'system',
  },
  {
    label: message('SEO'),
    to: 'seo',
    position: 6,
    icon: SearchIcon,
    group: 'system',
  },
];

const mergedNavConfig: SettingsNavItem[] = [...AppSettingsNavConfig];

for (const item of commonNavConfig) {
  if (!item) {
    continue;
  }
  const existing = mergedNavConfig.find(i => i.to === item.to);
  if (existing) {
    existing.group ??= item.group;
    existing.icon ??= item.icon;
    existing.position ??= item.position;
  } else {
    mergedNavConfig.push(item);
  }
}

export const SettingsNavConfig = mergedNavConfig;
